import type { UserAppearance } from "../utility/profileAppearance";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useFirebase } from "./FirebaseContext";
import { readIndexedDbValue, writeIndexedDbValue } from "../utility/indexedDbCache";
import { prepareBackground } from "../utility/prepareBackground";
import defaultBackground from "../images/background.webp";
import {
  getCachedOrFetch,
  invalidateCachedValue
} from "../utility/localDataCache";
import {
  getUserProfileCacheKey,
  USER_PROFILE_CACHE_OPTIONS
} from "../utility/profileCache";
import {
  readCachedBackgroundUrl,
  writeCachedBackgroundUrl
} from "../utility/backgroundUrlCache";
import { isDemoUserId } from "../utility/demoSession";
import { resolveUserBackgroundUrl } from "../utility/profileAppearance";

type BackgroundType = "app" | "profile";

interface ViewedProfileAppearance {
  userId: string;
  appBackgroundUrl: string | null | undefined;
  profileBackgroundUrl: string | null | undefined;
}

export interface UserBackgroundContextValue {
  appBackgroundUrl: string | null;
  profileBackgroundUrl: string | null;
  viewedProfileAppearance: ViewedProfileAppearance | null;
  activateViewedProfileAppearance: (userId?: string | null) => void;
  updateViewedProfileAppearance: (userId: string | null | undefined, userData?: UserAppearance) => void;
  deactivateViewedProfileAppearance: (userId?: string | null) => void;
  saveAppBackground: (file: File) => Promise<string>;
  saveProfileBackground: (file: File) => Promise<string>;
}

const UserBackgroundContext = createContext<UserBackgroundContextValue | null>(null);

export const UserBackgroundProvider = ({ children }: { children?: React.ReactNode }) => {
  const { auth, firebase, firestore, storage } = useFirebase();
  const [user] = useAuthState(auth);
  const [appBackgroundUrl, setAppBackgroundUrl] = useState<string | null>(defaultBackground);
  const [profileBackgroundUrl, setProfileBackgroundUrl] = useState<string | null>(defaultBackground);
  const [viewedProfileAppearance, setViewedProfileAppearance] = useState<ViewedProfileAppearance | null>(null);
  const objectUrls = useRef<Record<BackgroundType, string | null>>({ app: null, profile: null });

  const activateViewedProfileAppearance = useCallback((userId?: string | null) => {
    const normalizedUserId = String(userId || "").trim();
    if (!normalizedUserId) return;
    setViewedProfileAppearance((current) => current?.userId === normalizedUserId
      ? current
      : {
          userId: normalizedUserId,
          appBackgroundUrl: null,
          profileBackgroundUrl: null
        });
  }, []);

  const updateViewedProfileAppearance = useCallback((userId: string | null | undefined, userData: UserAppearance = {}) => {
    const normalizedUserId = String(userId || "").trim();
    if (!normalizedUserId) return;
    setViewedProfileAppearance({
      userId: normalizedUserId,
      appBackgroundUrl: resolveUserBackgroundUrl(userData, "app", defaultBackground),
      profileBackgroundUrl: resolveUserBackgroundUrl(userData, "profile", defaultBackground)
    });
  }, []);

  const deactivateViewedProfileAppearance = useCallback((userId?: string | null) => {
    const normalizedUserId = String(userId || "").trim();
    setViewedProfileAppearance((current) => (
      !normalizedUserId || current?.userId === normalizedUserId ? null : current
    ));
  }, []);

  const getBackgroundFields = (type: BackgroundType) => ({
    path: `${type}BackgroundPath`,
    url: `${type}BackgroundUrl`,
    updatedAt: `${type}BackgroundUpdatedAt`
  });

  const getCacheVersion = (value: unknown): number => {
    if (!value) return Date.now();
    if (typeof value === "object") {
      if ("toMillis" in value && typeof value.toMillis === "function") {
        const millis: unknown = value.toMillis();
        if (typeof millis === "number") return millis;
      }
      if ("seconds" in value && typeof value.seconds === "number" && value.seconds) {
        return value.seconds * 1000;
      }
    }
    const date = value instanceof Date ? value : new Date(
      typeof value === "string" || typeof value === "number" ? value : NaN
    );
    return Number.isNaN(date.getTime()) ? Date.now() : date.getTime();
  };

  const appendCacheBuster = (url: string | null | undefined, version = Date.now()): string => {
    if (!url) return defaultBackground;
    return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
  };

  const setBlobUrl = (type: BackgroundType, blob: Blob | null): string => {
    const currentUrl = objectUrls.current[type];
    if (currentUrl) URL.revokeObjectURL(currentUrl);
    const url = blob ? URL.createObjectURL(blob) : defaultBackground;
    objectUrls.current[type] = blob ? url : null;
    if (type === "app") setAppBackgroundUrl(url);
    else setProfileBackgroundUrl(url);
    return url;
  };

  const setRemoteUrl = (type: BackgroundType, url: string | null): string => {
    const currentUrl = objectUrls.current[type];
    if (currentUrl) URL.revokeObjectURL(currentUrl);
    objectUrls.current[type] = null;
    const nextUrl = url || defaultBackground;
    if (type === "app") setAppBackgroundUrl(nextUrl);
    else setProfileBackgroundUrl(nextUrl);
    return nextUrl;
  };

  const setBackgroundPending = (type: BackgroundType): void => {
    const currentUrl = objectUrls.current[type];
    if (currentUrl) URL.revokeObjectURL(currentUrl);
    objectUrls.current[type] = null;
    if (type === "app") setAppBackgroundUrl(null);
    else setProfileBackgroundUrl(null);
  };

  // localStorage is only a synchronous visual cache for an already known
  // public Storage URL. useLayoutEffect applies it before the browser paints
  // after Firebase restores auth, preventing a flash of defaultBackground.
  useLayoutEffect(() => {
    if (!user?.uid) return;
    const cachedAppBackground = readCachedBackgroundUrl(user.uid, "app");
    const cachedProfileBackground = readCachedBackgroundUrl(user.uid, "profile");
    if (cachedAppBackground) setRemoteUrl("app", cachedAppBackground);
    else setBackgroundPending("app");
    if (cachedProfileBackground) setRemoteUrl("profile", cachedProfileBackground);
  // These helpers only use stable React setters and objectUrls.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    let cancelled = false;
    const loadBackgrounds = async () => {
      if (!user?.uid) {
        setBlobUrl("app", null);
        setBlobUrl("profile", null);
        return;
      }
      const blobsPromise = Promise.all([
        readIndexedDbValue(`user-background:${user.uid}:app`).catch(() => null),
        readIndexedDbValue(`user-background:${user.uid}:profile`).catch(() => null)
      ]);
      let initialProfileApplied = false;
      let pendingBackgroundUpdate: Record<string, unknown> | null = null;

      const applyBackgrounds = async (userData: Record<string, unknown> = {}) => {
        const [appBlob, profileBlob] = await blobsPromise;
        if (cancelled) return;
        const appFields = getBackgroundFields("app");
        const profileFields = getBackgroundFields("profile");

        const appUrl = userData[appFields.url];
        const profileUrl = userData[profileFields.url];
        if (typeof appUrl === "string" && appUrl) {
          const nextUrl = appendCacheBuster(
            appUrl, getCacheVersion(userData[appFields.updatedAt])
          );
          writeCachedBackgroundUrl(user.uid, "app", nextUrl);
          setRemoteUrl(
            "app",
            nextUrl
          );
        } else if (readCachedBackgroundUrl(user.uid, "app")) {
          // A failed/offline profile refresh must not replace an already
          // painted custom background with the default asset.
          setRemoteUrl("app", readCachedBackgroundUrl(user.uid, "app"));
        } else {
          setBlobUrl("app", appBlob instanceof Blob ? appBlob : null);
        }

        if (typeof profileUrl === "string" && profileUrl) {
          const nextUrl = appendCacheBuster(
            profileUrl, getCacheVersion(userData[profileFields.updatedAt])
          );
          writeCachedBackgroundUrl(user.uid, "profile", nextUrl);
          setRemoteUrl(
            "profile",
            nextUrl
          );
        } else if (readCachedBackgroundUrl(user.uid, "profile")) {
          setRemoteUrl("profile", readCachedBackgroundUrl(user.uid, "profile"));
        } else {
          setBlobUrl("profile", profileBlob instanceof Blob ? profileBlob : null);
        }
      };

      const handleBackgroundUpdate = (userData: Record<string, unknown>) => {
        if (!initialProfileApplied) {
          pendingBackgroundUpdate = userData;
          return;
        }
        void applyBackgrounds(userData);
      };

      const userData = await getCachedOrFetch(
        getUserProfileCacheKey(user.uid),
        async () => {
          const snapshot = await firestore.collection("users").doc(user.uid).get();
          return snapshot.exists ? { uid: snapshot.id, ...snapshot.data() } : {};
        },
        {
          ...USER_PROFILE_CACHE_OPTIONS,
          onBackgroundUpdate: handleBackgroundUpdate
        }
      ).catch((error: unknown) => {
        console.warn("Не удалось обновить пользовательские фоны из Firestore:", error);
        return {};
      });
      await applyBackgrounds(userData);
      initialProfileApplied = true;
      if (pendingBackgroundUpdate) {
        await applyBackgrounds(pendingBackgroundUpdate);
      }
    };
    loadBackgrounds().catch((error: unknown) => console.warn("Не удалось загрузить пользовательские фоны:", error));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, firestore]);

  useEffect(() => () => {
    Object.values(objectUrls.current).forEach((url) => url && URL.revokeObjectURL(url));
  }, []);

  const saveBackground = async (type: BackgroundType, file: File): Promise<string> => {
    if (!user?.uid) throw new Error("Сначала войдите в аккаунт");
    if (isDemoUserId(user.uid)) {
      throw new Error("В демо-режиме нельзя менять фон профиля");
    }
    const blob = await prepareBackground(file, type);
    const backgroundPath = `users/${user.uid}/backgrounds/${type}.webp`;
    const backgroundRef = storage.ref(backgroundPath);
    await backgroundRef.put(blob, {
      contentType: "image/webp",
      // Every UI URL contains the Firestore update timestamp, so a changed
      // image receives a new cache key and unchanged backgrounds can safely be
      // reused instead of downloaded again on every visit.
      cacheControl: "public,max-age=31536000,immutable"
    });
    const backgroundUrl = await backgroundRef.getDownloadURL();
    const fields = getBackgroundFields(type);
    await firestore.collection("users").doc(user.uid).set(
      {
        [fields.path]: backgroundPath,
        [fields.url]: backgroundUrl,
        [fields.updatedAt]: firebase.firestore.FieldValue.serverTimestamp()
      },
      { merge: true }
    );
    invalidateCachedValue(getUserProfileCacheKey(user.uid));
    await writeIndexedDbValue(`user-background:${user.uid}:${type}`, blob).catch((error: unknown) => {
      console.warn("Не удалось сохранить фон в локальный кэш:", error);
    });
    const nextUrl = appendCacheBuster(backgroundUrl);
    writeCachedBackgroundUrl(user.uid, type, nextUrl);
    return setRemoteUrl(type, nextUrl);
  };

  const effectiveProfileBackgroundUrl = viewedProfileAppearance?.userId &&
    viewedProfileAppearance.userId !== user?.uid
    ? viewedProfileAppearance.profileBackgroundUrl || defaultBackground
    : profileBackgroundUrl;

  return (
    <UserBackgroundContext.Provider value={{
      appBackgroundUrl,
      profileBackgroundUrl: effectiveProfileBackgroundUrl,
      viewedProfileAppearance,
      activateViewedProfileAppearance,
      updateViewedProfileAppearance,
      deactivateViewedProfileAppearance,
      saveAppBackground: (file) => saveBackground("app", file),
      saveProfileBackground: (file) => saveBackground("profile", file)
    }}>
      {children}
    </UserBackgroundContext.Provider>
  );
};

export const useUserBackgrounds = (): UserBackgroundContextValue => {
  const context = useContext(UserBackgroundContext);
  if (!context) throw new Error("useUserBackgrounds must be used inside UserBackgroundProvider");
  return context;
};
